import React, { memo, useCallback, useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import {
  LayoutPage,
  StoryLayout,
  lineFont,
  placeLine,
  registerFonts,
  roundWords,
} from './pageLayout';

/** Desktop cap: an 8.5" page at 96 CSS px per inch. */
const MAX_PAGE_WIDTH = 816;
const PAGE_GAP = 24;

// The sheet previews print, so it stays white paper and black ink in either theme.
const PAPER = '#ffffff';
const INK = '#000000';

/** The nearest scrolling ancestor, so lazy pages load relative to what actually scrolls. */
const scrollParent = (el: HTMLElement | null): HTMLElement | null => {
  for (let node = el?.parentElement; node; node = node.parentElement) {
    const { overflowY } = window.getComputedStyle(node);
    if (overflowY === 'auto' || overflowY === 'scroll') return node;
  }
  return null;
};

const Sheet = memo(function Sheet({
  page,
  width,
  height,
  pxPerInch,
  render,
  label,
  register,
}: {
  page: LayoutPage;
  width: number;
  height: number;
  pxPerInch: number;
  /** Off-screen pages keep their size but skip their lines (a novel can run 300+ pages). */
  render: boolean;
  label: string;
  register: (i: number, el: HTMLDivElement | null) => void;
}) {
  return (
    <div
      ref={(el) => register(page.index, el)}
      data-page={page.index}
      role="group"
      aria-label={label}
      className="relative overflow-hidden mx-auto"
      style={{
        width,
        height,
        background: PAPER,
        color: INK,
        boxShadow: '0 1px 3px var(--ds-shadow), 0 6px 20px var(--ds-shadow)',
        border: '1px solid var(--ds-border)',
      }}
    >
      {render &&
        page.lines.map((line, i) => {
          const p = placeLine(line, pxPerInch);
          const font = lineFont(line);
          return (
            <span
              key={i}
              className="absolute"
              style={{
                left: p.left,
                top: p.top,
                fontSize: p.fontSize,
                lineHeight: 1,
                fontFamily: font.fontFamily,
                fontWeight: line.bold ? 700 : 400,
                fontStyle: line.italic ? 'italic' : 'normal',
                fontKerning: font.plain ? 'none' : undefined,
                fontVariantLigatures: font.plain ? 'none' : undefined,
                // Browsers half-trim full-width CJK punctuation by default; the PDF doesn't.
                ...(font.plain ? { textSpacingTrim: 'space-all' } : {}),
                textDecoration: line.underline ? 'underline' : 'none',
                transform: p.translateX === '0' ? undefined : `translateX(${p.translateX})`,
                whiteSpace: 'pre',
                // The API already ordered the runs; a right-to-left one draws its characters
                // reversed (brackets mirrored), exactly as the PDF does.
                ...(line.dir === 'rtl' ? { direction: 'rtl', unicodeBidi: 'bidi-override' } : {}),
              }}
            >
              {line.text}
            </span>
          );
        })}
    </div>
  );
});

/**
 * The story as it prints: the API lays out pages in the format's industry layout
 * (GET /stories/:id/layout, the same JSON the PDF is drawn from) and this places each line.
 * Refetches whenever `refreshKey` changes — the workspace bumps it after reloading the story.
 */
const PageView: React.FC<{ storyId: string; format: string; refreshKey: number }> = ({
  storyId,
  format,
  refreshKey,
}) => {
  const { t, i18n } = useTranslation();
  const [layout, setLayout] = useState<StoryLayout | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [width, setWidth] = useState(0);
  // Pages near the viewport; the first few render before the observer reports.
  const [near, setNear] = useState<Set<number>>(() => new Set([1, 2, 3]));
  const containerRef = useRef<HTMLDivElement>(null);
  const pageEls = useRef(new Map<number, HTMLDivElement>());
  const observer = useRef<IntersectionObserver | null>(null);
  const requestSeq = useRef(0);

  const load = useCallback(async () => {
    const seq = ++requestSeq.current;
    setLoading(true);
    setError(false);
    try {
      const res = await axios.get<StoryLayout>(`/stories/${storyId}/layout`);
      if (seq !== requestSeq.current) return;
      setLayout(res.data);
    } catch {
      if (seq !== requestSeq.current) return;
      setError(true);
    } finally {
      if (seq === requestSeq.current) setLoading(false);
    }
  }, [storyId]);

  useEffect(() => {
    load();
  }, [load, refreshKey]);

  // The PDF's own fonts, served by the API, so the sheet draws what prints.
  useEffect(() => {
    registerFonts(layout?.fonts, axios.defaults.baseURL ?? '/api');
  }, [layout]);

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    setWidth(el.clientWidth);
    if (typeof ResizeObserver === 'undefined') return;
    const ro = new ResizeObserver((entries) => setWidth(entries[0].contentRect.width));
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  const hasPages = !!layout?.pages.length && width > 0;
  useEffect(() => {
    if (!hasPages) return;
    if (typeof IntersectionObserver === 'undefined') {
      setNear(new Set(layout!.pages.map((p) => p.index)));
      return;
    }
    const io = new IntersectionObserver(
      (entries) =>
        setNear((prev) => {
          const next = new Set(prev);
          let changed = false;
          for (const e of entries) {
            const i = Number((e.target as HTMLElement).dataset.page);
            if (e.isIntersecting ? !next.has(i) : next.has(i)) {
              if (e.isIntersecting) next.add(i);
              else next.delete(i);
              changed = true;
            }
          }
          return changed ? next : prev;
        }),
      // Two screens either side, so a fast scroll rarely meets a blank sheet.
      { root: scrollParent(containerRef.current), rootMargin: '200% 0px' },
    );
    observer.current = io;
    pageEls.current.forEach((el) => io.observe(el));
    return () => {
      io.disconnect();
      observer.current = null;
    };
  }, [hasPages, layout]);

  const register = useCallback((i: number, el: HTMLDivElement | null) => {
    const prev = pageEls.current.get(i);
    if (prev === el) return;
    if (prev) observer.current?.unobserve(prev);
    if (el) {
      pageEls.current.set(i, el);
      observer.current?.observe(el);
    } else {
      pageEls.current.delete(i);
    }
  }, []);

  const num = (n: number) => new Intl.NumberFormat(i18n.language).format(n);
  const pageCount = layout?.pages.length ?? 0;
  const stats = layout
    ? [
        pageCount === 1
          ? t('stories.pageView.onePage', '1 page')
          : t('stories.pageView.pages', '{{count}} pages', { count: pageCount }),
        // A screenplay page runs about a minute; scripts aren't measured in words.
        format === 'screenplay' &&
          t('stories.pageView.runtime', '≈ {{minutes}} min', {
            minutes: num(layout.pages.filter((p) => !p.titlePage).length),
          }),
        layout.kind !== 'screenplay' &&
          layout.kind !== 'stage_play' &&
          t('stories.pageView.aboutWords', 'about {{words}} words', {
            words: num(roundWords(layout.wordCount)),
          }),
      ].filter(Boolean)
    : [];

  const sheetWidth = Math.min(width, MAX_PAGE_WIDTH);
  const pxPerInch = layout ? sheetWidth / layout.pageWidth : 0;
  const sheetHeight = layout ? sheetWidth * (layout.pageHeight / layout.pageWidth) : 0;

  return (
    <div ref={containerRef} className="w-full">
      {!layout && loading && (
        <div className="flex flex-col items-center justify-center py-16 gap-3">
          <div
            className="animate-spin rounded-full h-8 w-8 border-b-2"
            style={{ borderColor: 'var(--ds-accent, #8b5cf6)' }}
          />
          <p className="text-sm" style={{ color: 'var(--ds-text-secondary)' }}>
            {t('stories.pageView.loading', 'Laying out pages…')}
          </p>
        </div>
      )}

      {!layout && !loading && error && (
        <div
          className="ds-card rounded-xl p-5 text-center space-y-3"
          style={{ background: 'var(--ds-bg-card)', border: '1px solid var(--ds-border)' }}
        >
          <p className="text-sm" style={{ color: 'var(--ds-text-secondary)' }}>
            {t('stories.pageView.error', "Couldn't lay out the pages.")}
          </p>
          <button
            onClick={load}
            className="px-4 py-2 rounded-lg text-sm font-medium border transition-colors hover:border-primary-500"
            style={{ borderColor: 'var(--ds-border)', color: 'var(--ds-text-primary)' }}
          >
            {t('stories.pageView.retry', 'Try again')}
          </button>
        </div>
      )}

      {layout && pageCount === 0 && (
        <p className="text-sm text-center py-12" style={{ color: 'var(--ds-text-secondary)' }}>
          {t(
            'stories.pageView.empty',
            'Nothing to print yet — write a scene and it appears here as finished pages.',
          )}
        </p>
      )}

      {layout && pageCount > 0 && (
        <>
          <div
            className="flex flex-wrap items-baseline justify-between gap-x-3 gap-y-1 mb-3 mx-auto"
            style={{ maxWidth: sheetWidth || undefined }}
          >
            <span className="text-sm font-medium" style={{ color: 'var(--ds-text-primary)' }}>
              {stats.join(' · ')}
            </span>
            <span className="text-xs" style={{ color: 'var(--ds-text-secondary)' }}>
              {error
                ? t('stories.pageView.refreshError', "Couldn't refresh — showing the last layout.")
                : t('stories.pageView.caption', 'Exactly as the PDF prints.')}
            </span>
          </div>
          {sheetWidth > 0 && (
            <div className="flex flex-col" style={{ gap: PAGE_GAP }}>
              {layout.pages.map((page) => (
                <Sheet
                  key={page.index}
                  page={page}
                  width={sheetWidth}
                  height={sheetHeight}
                  pxPerInch={pxPerInch}
                  render={near.has(page.index)}
                  label={t('stories.pageView.pageLabel', 'Page {{number}}', {
                    number: page.index,
                  })}
                  register={register}
                />
              ))}
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default PageView;
